import { useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { useStudio } from "@/context/StudioContext";
import { PageHeader, Panel, Loading, StatCard } from "@/components/common";
import { Buildings, Plus, CurrencyDollar, Gauge, TrendUp, Eye, FilmSlate, Scissors, ArrowRight } from "@phosphor-icons/react";

export default function AllStudios() {
  const { data, loading } = useFetch("/overview");
  const { switchStudio } = useStudio();
  const navigate = useNavigate();
  if (loading || !data) return <Loading label="Loading My AI Studios…" />;
  const t = data.totals;

  const view = (id) => { switchStudio(id); navigate("/"); };

  return (
    <div>
      <PageHeader icon={Buildings} title="My AI Studios"
        subtitle="Owner-level view across every animation brand. Each studio is fully isolated but shares one AI production engine."
        actions={<button data-testid="create-studio-btn" onClick={() => navigate("/studios/new")}
          className="inline-flex items-center gap-2 rounded-full bg-magic px-5 py-2.5 font-semibold text-black hover:opacity-90 transition-opacity"><Plus size={18} weight="bold" /> Create New Studio</button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Revenue" value={`$${t.revenue.toFixed(2)}`} icon={CurrencyDollar} accent="#00FF9D" sub="connect platforms for real data" />
        <StatCard label="Total AI Costs" value={`$${t.ai_costs.toFixed(2)}`} icon={Gauge} accent="#FFB800" />
        <StatCard label="Estimated Profit" value={`$${t.estimated_profit.toFixed(2)}`} icon={TrendUp} accent={t.estimated_profit >= 0 ? "#00FF9D" : "#FF3366"} />
        <StatCard label="Total Views" value={t.views.toLocaleString()} icon={Eye} accent="#00E5FF" sub="no real data yet" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Subscribers" value={t.subscribers} accent="#8B5CF6" />
        <StatCard label="Content Produced" value={t.episodes + t.shorts} sub={`${t.episodes} eps · ${t.shorts} shorts`} accent="#00E5FF" />
        <StatCard label="Scheduled" value={t.scheduled} accent="#FFB800" />
        <StatCard label="Studios" value={data.studios.length} icon={Buildings} accent="#FF007F" />
      </div>

      <h3 className="font-display text-lg font-bold mb-4">Studios</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {data.studios.map((s) => (
          <Panel key={s.id} data-testid={`studio-card-${s.id}`} hover className="relative overflow-hidden">
            <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-20 blur-3xl" style={{ background: s.color }} />
            <div className="flex items-center gap-3 mb-4">
              <span className="h-11 w-11 rounded-xl flex items-center justify-center text-2xl" style={{ background: `${s.color}22`, border: `1px solid ${s.color}55` }}>{s.emoji}</span>
              <div>
                <div className="font-display text-lg font-bold text-white">{s.name}</div>
                <div className="text-xs text-zinc-500">{s.genre || "Studio"}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <Mini label="Revenue" value={`$${s.revenue.toFixed(0)}`} color="#00FF9D" />
              <Mini label="Costs" value={`$${s.costs.toFixed(0)}`} color="#FFB800" />
              <Mini label="Profit" value={`$${s.profit.toFixed(0)}`} color={s.profit >= 0 ? "#00FF9D" : "#FF3366"} />
            </div>
            <div className="flex items-center gap-4 text-xs text-zinc-400 mb-4">
              <span className="flex items-center gap-1"><FilmSlate size={14} weight="duotone" /> {s.episodes} eps</span>
              <span className="flex items-center gap-1"><Scissors size={14} weight="duotone" /> {s.shorts} shorts</span>
              <span className="flex items-center gap-1"><Eye size={14} weight="duotone" /> {s.views}</span>
            </div>
            <button data-testid={`view-studio-${s.id}`} onClick={() => view(s.id)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white/5 border border-white/10 py-2.5 text-sm text-white hover:border-white/30 transition-colors">
              View Studio <ArrowRight size={15} weight="bold" />
            </button>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div className="rounded-lg border border-white/8 bg-black/20 p-2 text-center">
      <div className="font-display font-bold text-sm" style={{ color }}>{value}</div>
      <div className="text-[10px] text-zinc-500">{label}</div>
    </div>
  );
}
